import React, { useRef, useEffect } from "react";

const ForestFireCanvasDynamic = ({
  rows = 60,
  cols = 60,
  cellSize = window.innerWidth/cols,
  interval = 100,
  alpha = 2.5,
  growProb = 0.01, // probability an empty cell grows a tree per step
  lightningProb = 0.002 // probability of random lightning strike per cell per step
}) => {
  const canvasRef = useRef(null);

  // Generate power-law flammability
  const powerLawProbability = (alpha) => {
    const r = Math.random();
    return Math.min(Math.pow(r, 1 / (1 - alpha)), 0.99);
  };

  // Initialize forest grid
  const grid = useRef(
    Array.from({ length: rows }, () =>
      Array.from({ length: cols }, () => ({
        state: "tree", // tree, burning, empty
        flammability: powerLawProbability(alpha)
      }))
    )
  );

  // Ignite a random tree
  const igniteTree = () => {
    const i = Math.floor(Math.random() * rows);
    const j = Math.floor(Math.random() * cols);
    if (grid.current[i][j].state === "tree") {
      grid.current[i][j].state = "burning";
    }
  };

  // Spread fire step
  const spreadFire = () => {
    const newGrid = grid.current.map((row) =>
      row.map((cell) => ({ ...cell }))
    );

    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        const cell = grid.current[i][j];

        if (cell.state === "burning") {
          newGrid[i][j].state = "empty";

          const neighbors = [
            [i - 1, j],
            [i + 1, j],
            [i, j - 1],
            [i, j + 1]
          ];

          neighbors.forEach(([x, y]) => {
            if (x >= 0 && x < rows && y >= 0 && y < cols) {
              if (newGrid[x][y].state === "tree") {
                if (Math.random() < grid.current[x][y].flammability) {
                  newGrid[x][y].state = "burning";
                }
              }
            }
          });
        } else if (cell.state === "empty") {
          // Tree can regrow
          if (Math.random() < growProb) {
            newGrid[i][j].state = "tree";
            newGrid[i][j].flammability = powerLawProbability(alpha);
          }
        }

        // Lightning strike
        if (cell.state === "tree" && Math.random() < lightningProb) {
          newGrid[i][j].state = "burning";
        }
      }
    }

    grid.current = newGrid;
    drawGrid();
  };

  // Draw grid on canvas
  const drawGrid = () => {
    const ctx = canvasRef.current.getContext("2d");
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        const cell = grid.current[i][j];
        switch (cell.state) {
          case "tree":
            ctx.fillStyle = "green";
            break;
          case "burning":
            ctx.fillStyle = "red";
            break;
          case "empty":
            ctx.fillStyle = "black";
            break;
        }
        ctx.fillRect(j * cellSize, i * cellSize, cellSize, cellSize);
      }
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = cols * cellSize;
    canvas.height = rows * cellSize;
    igniteTree();
    drawGrid();
    const timer = setInterval(spreadFire, interval);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="letter-grid">
      <canvas ref={canvasRef} style={{ border: "1px solid #333" }} />
    </div>);
};

export default ForestFireCanvasDynamic;
